import {
  MT_PREPARE_BUCKET_COUNTS_API,
  MT_PREPARE_CALCULATE_RATE_API,
  MT_PREPARE_EXCLUSION_API,
  MT_PREPARE_EXPORT_API,
  MT_PREPARE_INLINE_EDIT_API,
  MT_PREPARE_NEGATIVE_ENTRIES_API,
  MT_PREPARE_PERIOD_OPTIONS_API,
  MT_PREPARE_RATE_DETERMINATION_STATUS_API,
  MT_PREPARE_REPROCESS_API,
  MT_PREPARE_SCREEN_MAP,
  MT_PREPARE_TOTAL_SUM_API,
  MT_PREPARE_TRANSACTIONAL_DATA_API,
  MT_TRANSACTION_DATA_META_DATA,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
  mtBuildRequestHeader,
  mtHandleErrorResponse,
  mtHandleRequestFailure,
  mtHandleSuccessResponse,
  mtParseJsonResponse,
  mtParseMessageResponse,
  mtUrlBuilder,
} from '@/multi-territory/mt-services/mt-api-utils'

function buildPrepareHeaders(countryCode: string): Record<string, string> {
  return { 'X-Country-Code': countryCode }
}

export const fetchMTTransactionDataMetadata = async (
  countryCode: string,
  screenName: string
) => {
  const screen = MT_PREPARE_SCREEN_MAP[screenName] ?? `PREPARE_${screenName}`
  const url = mtUrlBuilder(
    MT_TRANSACTION_DATA_META_DATA(countryCode),
    {},
    { screen }
  )
  const options = mtBuildRequestHeader(
    'GET',
    null,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export const fetchMTPrepareTransactionalData = async (
  payload: unknown,
  bucket: string,
  countryCode: string
) => {
  const url = mtUrlBuilder(MT_PREPARE_TRANSACTIONAL_DATA_API(bucket))
  const headers = mtBuildRequestHeader(
    'POST',
    payload,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, headers)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtHandleSuccessResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export const fetchMTPrepareBucketCounts = async (
  payload: unknown,
  countryCode: string
) => {
  const url = mtUrlBuilder(MT_PREPARE_BUCKET_COUNTS_API)
  const headers = mtBuildRequestHeader(
    'POST',
    payload,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, headers)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtHandleSuccessResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export const fetchMTPrepareTotalSum = async (
  payload: unknown,
  bucket: string,
  countryCode: string
) => {
  const url = mtUrlBuilder(MT_PREPARE_TOTAL_SUM_API(bucket))
  const headers = mtBuildRequestHeader(
    'POST',
    payload,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, headers)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtHandleSuccessResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export const fetchMTPrepareNegativeEntries = async (
  payload: unknown,
  countryCode: string
) => {
  const url = mtUrlBuilder(MT_PREPARE_NEGATIVE_ENTRIES_API)
  const headers = mtBuildRequestHeader(
    'POST',
    payload,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, headers)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtHandleSuccessResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export const mtExportPrepareData = async (
  payload: unknown,
  bucket: string,
  countryCode: string
) => {
  const url = mtUrlBuilder(MT_PREPARE_EXPORT_API(bucket))
  const headers = mtBuildRequestHeader(
    'POST',
    payload,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, headers)
    if (!response.ok) return await mtHandleRequestFailure(response)
    // Export API returns a JSON body with the generated report id, e.g.
    // { "data": 712, "success": true } — not a file blob. Unwrap the nested
    // `data` key so the caller receives the report id directly.
    return await mtParseJsonResponse<number>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export const mtReprocessPrepare = async (
  payload: unknown,
  bucket: string,
  countryCode: string
) => {
  const url = mtUrlBuilder(MT_PREPARE_REPROCESS_API(bucket))
  const headers = mtBuildRequestHeader(
    'POST',
    payload,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, headers)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtHandleSuccessResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export const mtExclusionAction = async (
  uuids: string[],
  bucket: string,
  countryCode: string
) => {
  const url = mtUrlBuilder(MT_PREPARE_EXCLUSION_API(bucket))
  const headers = mtBuildRequestHeader(
    'PUT',
    uuids,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, headers)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtHandleSuccessResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export type MTCalculateRatePayload = {
  entityTans: string[]
  periodFilters: string[]
  uploadCategory: string
}

/**
 * Trigger WHT/TDS rate determination for the selected entities and periods.
 * Backing endpoint: POST wht-mt-data-management/api/v1/fileUpload/calculateRateRequest
 * Returns a message string, e.g. "Rate determination triggered for the selected period".
 */
export const mtCalculateRateRequest = async (
  payload: MTCalculateRatePayload,
  countryCode: string
) => {
  const url = mtUrlBuilder(MT_PREPARE_CALCULATE_RATE_API)
  const options = mtBuildRequestHeader(
    'POST',
    payload,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export type MTRateDeterminationStatusPayload = {
  entityTans: string[]
  periodFilters: string[]
  uploadCategory: string
}

export type MTRateDeterminationStatus = {
  rateDeterminationInProgress: boolean
  inProgressCount: number
  buttonEnabled: boolean
  /**
   * True while an inline-edit / reprocess lock is held on the selected
   * entity+period. When set, the Edit button must be disabled so a second
   * edit/reprocess can't start until the current one finishes.
   */
  reprocessLockInProgress?: boolean
}

/**
 * Fetch the current rate-determination status for the selected entities/periods.
 * Drives whether the "Suggest WHT Rate" button is enabled or disabled — call this
 * alongside every VTD (transactional-data) list fetch.
 * Backing endpoint: POST wht-mt-data-management/api/v1/rate-determination/status
 * Response: { data: { rateDeterminationInProgress, inProgressCount, buttonEnabled }, success }
 */
export const fetchMTRateDeterminationStatus = async (
  payload: MTRateDeterminationStatusPayload,
  countryCode: string
) => {
  const url = mtUrlBuilder(MT_PREPARE_RATE_DETERMINATION_STATUS_API)
  const options = mtBuildRequestHeader(
    'POST',
    payload,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTRateDeterminationStatus>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// ─── Inline (bulk) edit ─────────────────────────────────────────────────────

/** A single row edit — only the changed cells are sent. */
export type MTPrepareInlineEdit = {
  uuid: string
  changedCells: Record<string, string | number | null>
}

/** Per-row validation failure returned by the inline-edit endpoint. */
export type MTPrepareInlineValidationError = {
  uuid?: string
  field?: string
  message?: string
  errorMessage?: string
  [key: string]: unknown
}

/** Result body of a successful inline-edit call. */
export type MTPrepareInlineEditResult = {
  status: string
  successCount: number
  errorCount: number
  validationErrors: MTPrepareInlineValidationError[]
  message: string
}

/**
 * Bulk/inline edit transactional-data rows for a bucket.
 * Only changed cells are sent per row.
 * Backing endpoint: POST wht-mt-data-management/api/v1/prepare/transactional-data/inline-edit/{bucket}
 * Response: { data: { status, successCount, errorCount, validationErrors, message }, success }
 */
export const mtInlineEditPrepareData = async (
  edits: MTPrepareInlineEdit[],
  bucket: string,
  countryCode: string
) => {
  const url = mtUrlBuilder(MT_PREPARE_INLINE_EDIT_API(bucket))
  const options = mtBuildRequestHeader(
    'PUT',
    { edits },
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTPrepareInlineEditResult>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

export const fetchMTPreparePeriodOptions = async (countryCode: string) => {
  const url = mtUrlBuilder(MT_PREPARE_PERIOD_OPTIONS_API)
  const options = mtBuildRequestHeader(
    'GET',
    null,
    buildPrepareHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtHandleSuccessResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
